/**
 * Calculate the next occurrence date for a recurring transaction.
 *
 * - Monthly (months is null/empty): returns the next occurrence of `dayOfMonth`
 * - Yearly (months specified): returns the next occurrence from `months`
 */
export function getNextOccurrenceDate(
  months: number[] | null,
  dayOfMonth: number,
  today: Date = new Date(),
): string {
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth() + 1
  const currentDay = today.getDate()
  const pad = (n: number) => String(n).padStart(2, '0')

  // Clamp dayOfMonth to the last day of the given month/year
  // new Date(year, month, 0) returns the last day of that month
  const clampDay = (year: number, month: number) =>
    Math.min(dayOfMonth, new Date(year, month, 0).getDate())

  // Monthly: find the next occurrence of this day
  if (!months || months.length === 0) {
    if (dayOfMonth >= currentDay) {
      const day = clampDay(currentYear, currentMonth)
      return `${currentYear}/${pad(currentMonth)}/${pad(day)}`
    }
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1
    const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear
    const day = clampDay(nextYear, nextMonth)
    return `${nextYear}/${pad(nextMonth)}/${pad(day)}`
  }

  // Yearly: find the next occurrence from frequency_months
  const sorted = [...months].sort((a, b) => a - b)
  for (const m of sorted) {
    if (m > currentMonth || (m === currentMonth && dayOfMonth >= currentDay)) {
      const day = clampDay(currentYear, m)
      return `${currentYear}/${pad(m)}/${pad(day)}`
    }
  }
  const day = clampDay(currentYear + 1, sorted[0])
  return `${currentYear + 1}/${pad(sorted[0])}/${pad(day)}`
}
